import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import { dialogs } from "../../context/DialogContext";
import { nombreDocumentoLegible } from "../../utils/gestionVentasQuickReplies";
import ModalVistaPreviaDocumentoNas, { IconoLupa, prefetchNasPreview } from "./ModalVistaPreviaDocumentoNas";

function fmtFecha(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function VisorDocumentosNasExpediente({
  expedienteId,
  numeroExpediente,
  compradores = [],
  propietarios = [],
  resolverContactos,
  onSelDocsChange,
  onAbrirWhatsApp,
  onAbrirEmail,
  archivosRefreshKey,
  accent,
  tx,
  isDark,
  btnSm,
  thCol,
  td,
  inpCompact,
  panelCol,
  panelTitleCol,
  puedeModificar = true,
}) {
  const [archivos, setArchivos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [selDocs, setSelDocs] = useState(() => new Set());
  const [destTipo, setDestTipo] = useState("comprador");
  const [previewDoc, setPreviewDoc] = useState(null);

  const cargar = useCallback(async () => {
    if (!expedienteId) {
      setArchivos([]);
      return;
    }
    setCargando(true);
    try {
      const r = await api.get(`/expedientes/${expedienteId}/archivos-nas`);
      setArchivos(r.data?.data?.archivos || []);
    } catch {
      setArchivos([]);
    } finally {
      setCargando(false);
    }
  }, [expedienteId]);

  useEffect(() => {
    setSelDocs(new Set());
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!archivosRefreshKey) return;
    cargar();
  }, [archivosRefreshKey, cargar]);

  useEffect(() => {
    onSelDocsChange?.([...selDocs]);
  }, [selDocs, onSelDocsChange]);

  const toggleDoc = (nombre) => {
    setSelDocs((prev) => {
      const next = new Set(prev);
      if (next.has(nombre)) next.delete(nombre);
      else next.add(nombre);
      return next;
    });
  };

  const contactosDestino = async () => {
    const compIdx = destTipo === "vendedor"
      ? new Set()
      : new Set(compradores.map((_, i) => i));
    const propIdx = destTipo === "comprador"
      ? new Set()
      : new Set(propietarios.map((_, i) => i));
    const contactos = await resolverContactos(compIdx, propIdx);
    if (!contactos?.length) {
      await dialogs.alert("No hay contactos con teléfono o email para el destinatario seleccionado.");
      return null;
    }
    return contactos;
  };

  const validarEnvio = () => {
    if (!expedienteId) {
      dialogs.alert("Guarda el expediente antes de enviar documentos.");
      return null;
    }
    const docs = [...selDocs];
    if (!docs.length) {
      dialogs.alert("Selecciona al menos un documento.");
      return null;
    }
    return docs;
  };

  const solicitarWhatsApp = async () => {
    const docs = validarEnvio();
    if (!docs) return;
    const contactos = await contactosDestino();
    if (!contactos) return;
    const conTel = contactos.filter((c) => c.telefono);
    if (!conTel.length) {
      await dialogs.alert("Ningún contacto del destinatario tiene teléfono.");
      return;
    }
    onAbrirWhatsApp?.(conTel, docs);
  };

  const solicitarEmail = async () => {
    const docs = validarEnvio();
    if (!docs) return;
    const contactos = await contactosDestino();
    if (!contactos) return;
    const conMail = contactos.filter((c) => c.email);
    if (!conMail.length) {
      await dialogs.alert("Ningún contacto del destinatario tiene email.");
      return;
    }
    onAbrirEmail?.(conMail, docs);
  };

  const th = thCol(accent);
  const destBtn = (val, label) => ({
    ...btnSm,
    padding: "2px 6px",
    fontSize: 10,
    fontWeight: 700,
    background: destTipo === val
      ? accent
      : (isDark ? "#1a2030" : "#fff"),
    color: destTipo === val ? "#fff" : tx,
    border: `1px solid ${destTipo === val ? accent : (isDark ? "#4a5568" : "#cbd5e1")}`,
  });

  return (
    <div style={{ ...panelCol(accent), alignSelf: "stretch", minWidth: 0 }}>
      <div style={panelTitleCol(accent)}>
        <span>DOCUMENTOS</span>
        <button
          type="button"
          style={{ ...btnSm, padding: "1px 6px", fontSize: 10, lineHeight: 1.2 }}
          onClick={cargar}
          disabled={!expedienteId || cargando || !puedeModificar}
          title="Actualizar listado"
        >
          ↻
        </button>
      </div>
      <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: tx }}>Para:</span>
        {[
          { v: "comprador", l: "Comprador" },
          { v: "vendedor", l: "Vendedor" },
          { v: "ambos", l: "Ambos" },
        ].map(({ v, l }) => (
          <button key={v} type="button" style={destBtn(v, l)} onClick={() => setDestTipo(v)} disabled={!puedeModificar}>
            {l}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
        {!expedienteId ? (
          <div style={{ fontSize: 10, color: tx, opacity: 0.7, padding: 4 }}>Guarda el expediente para ver la carpeta NAS.</div>
        ) : cargando ? (
          <div style={{ fontSize: 10, color: tx, opacity: 0.7, padding: 4 }}>Cargando…</div>
        ) : !archivos.length ? (
          <div style={{ fontSize: 10, color: tx, opacity: 0.7, padding: 4 }}>Sin archivos en la carpeta del expediente.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: 10 }}>
            <colgroup>
              <col style={{ width: 22 }} />
              <col />
              <col style={{ width: 52 }} />
              <col style={{ width: 58 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={th} />
                <th style={th}>Nombre</th>
                <th style={th}>Fecha</th>
                <th style={th}>Tipo</th>
              </tr>
            </thead>
            <tbody>
              {archivos.map((a) => (
                <tr key={a.nombre}>
                  <td style={td}>
                    <input
                      type="checkbox"
                      checked={selDocs.has(a.nombre)}
                      onChange={() => toggleDoc(a.nombre)}
                      title="Seleccionar documento"
                      disabled={!puedeModificar}
                    />
                  </td>
                  <td style={{ ...td, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.nombre}>
                    {nombreDocumentoLegible(a.nombre)}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap", fontSize: 9 }}>{fmtFecha(a.fecha)}</td>
                  <td style={{ ...td, whiteSpace: "nowrap", fontSize: 9 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{a.tipo || "—"}</span>
                      <button
                        type="button"
                        title="Ver documento"
                        onMouseEnter={() => {
                          if (expedienteId && a.nombre) void prefetchNasPreview(expedienteId, a.nombre);
                        }}
                        onFocus={() => {
                          if (expedienteId && a.nombre) void prefetchNasPreview(expedienteId, a.nombre);
                        }}
                        onClick={() => setPreviewDoc(a.nombre)}
                        style={{
                          flexShrink: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                          width: 18,
                          height: 18,
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          color: accent,
                          opacity: 0.9,
                        }}
                      >
                        <IconoLupa size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 4, flexShrink: 0 }}>
        <button
          type="button"
          style={{ ...btnSm, flex: 1, color: "#16a34a", fontSize: 11 }}
          onClick={solicitarWhatsApp}
          disabled={!expedienteId || !puedeModificar}
        >
          WhatsApp
        </button>
        <button
          type="button"
          style={{ ...btnSm, flex: 1, fontSize: 11 }}
          onClick={solicitarEmail}
          disabled={!expedienteId || !puedeModificar}
        >
          Email
        </button>
      </div>
      <ModalVistaPreviaDocumentoNas
        isOpen={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        expedienteId={expedienteId}
        nombreArchivo={previewDoc}
        isDark={isDark}
        tx={tx}
      />
    </div>
  );
}
